import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

/* Shown automatically when the signed-in user must rotate a one-time password; cannot be dismissed. */
export default function ForcePasswordChange() {
  const { user, changePassword } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const open = Boolean(user && user.must_change_password);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) { setError("New passwords do not match"); return; }
    setBusy(true);
    const res = await changePassword(form.current, form.next);
    setBusy(false);
    if (res.ok) toast.success("Password updated — you're all set");
    else setError(res.error);
  };

  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="force-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Set a new password</DialogTitle>
          <DialogDescription>Your account was created with a one-time password. Choose your own before continuing.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label htmlFor="fp-current" className="text-xs">Current password</Label><Input id="fp-current" type="password" autoComplete="current-password" value={form.current} onChange={set("current")} required data-testid="force-password-current" /></div>
          <div><Label htmlFor="fp-next" className="text-xs">New password</Label><Input id="fp-next" type="password" autoComplete="new-password" value={form.next} onChange={set("next")} required minLength={8} data-testid="force-password-new" /></div>
          <div><Label htmlFor="fp-confirm" className="text-xs">Confirm new password</Label><Input id="fp-confirm" type="password" autoComplete="new-password" value={form.confirm} onChange={set("confirm")} required data-testid="force-password-confirm" /></div>
          <p className="text-xs text-slate-500">At least 8 characters with letters and numbers. All other devices will be signed out.</p>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="force-password-error">{error}</div>}
          <Button type="submit" disabled={busy} className="w-full bg-slate-950 hover:bg-slate-800" data-testid="force-password-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
